import React from 'react';

import { cn } from '../cn';
import Label from '../Input2/Label';
import Textarea from '../Input2/Textarea';

function useDebouncedCallback<TArgs extends unknown[]>(callback: (...args: TArgs) => void, wait: number) {
  const timeout = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = React.useRef<{ args: TArgs; callback: (...args: TArgs) => void } | null>(null);

  const flush = React.useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
    if (pending.current) {
      const { args, callback: pendingCallback } = pending.current;
      pending.current = null;
      pendingCallback(...args);
    }
  }, []);

  React.useEffect(() => {
    return () => {
      flush();
    };
  }, [flush]);

  const schedule = React.useCallback(
    (...args: TArgs) => {
      if (timeout.current) clearTimeout(timeout.current);
      pending.current = { args, callback };
      timeout.current = setTimeout(() => {
        timeout.current = undefined;
        pending.current = null;
        callback(...args);
      }, wait);
    },
    [callback, wait],
  );

  return { schedule, flush };
}

export type SidebarNotesProps = {
  recordId: string;
  title?: string;
  initialValue?: string;
  onDebounceChange?: (value: string) => void;
};

export function SidebarNotes({ recordId, title, initialValue, onDebounceChange }: SidebarNotesProps) {
  const [value, setValue] = React.useState<string>(initialValue || '');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const { schedule: debounced, flush } = useDebouncedCallback((v: string) => onDebounceChange?.(v), 1000);

  const prevRecordId = React.useRef(recordId);
  if (prevRecordId.current !== recordId) {
    prevRecordId.current = recordId;
    setValue(initialValue || '');
  }

  React.useEffect(() => {
    flush();
  }, [recordId, flush]);

  const onChangeValue = (newValue: string) => {
    setValue(newValue);
    debounced(newValue);
  };

  const handleContainerClick = () => {
    textareaRef.current?.focus();
  };

  const hasValue = value.trim().length > 0;

  return (
    <div className={cn('relative', 'border-y border-quarter-spanish-white-400 dark:border-quarter-spanish-white-600')}>
      <div
        className="relative bg-quarter-spanish-white-50 dark:bg-quarter-spanish-white-900 text-quarter-spanish-white-800 dark:text-quarter-spanish-white-200 cursor-text"
        onClick={handleContainerClick}
      >
        <Label size="2xs" color="yellow" className="absolute top-1.5 left-3 uppercase tracking-wider pointer-events-none">
          {title}
        </Label>
        <div className="pl-1">
          <Textarea
            ref={textareaRef}
            size="sm"
            color="yellow" tone="outline"
            shape="square"
            value={value}
            onChange={(e) => onChangeValue(e.target.value)}
            bordered={false}
            className="w-full pt-5 bg-transparent!"
          />
        </div>
      </div>
      <div
        className={cn(
          'absolute -inset-y-px left-0 w-1 transition-all duration-200',
          hasValue ? 'bg-quarter-spanish-white-500 dark:bg-quarter-spanish-white-400' : 'w-0 bg-transparent',
        )}
      />
    </div>
  );
}

export default SidebarNotes;
